// The clipboard of selected objects: copied, held per space (model, sheet,
// title block) and handed to the system clipboard, pasted back as a load with
// each copy's layer kept. docs/claude/selection.md.
import {
  type DrawingGroup,
  type Vec2,
} from '../../shared/types';
import { selectedObjects } from '../selection';
import {
  clipboardHasContent,
  type ClipboardPayload,
  deserializeClipboard,
  serializeClipboard,
} from '../clipboardSerial';
import { objConstraints } from './constraintUi';
import {
  flashStatus,
  layers,
  state,
} from './core';
import {
  copiesOfSelection,
  emptyObjectArrays,
  objectsOf,
  STORED_KINDS,
} from '../drawingObjects';
import {
  groupables,
  groupsForCopy,
} from '../groups';
import { tr } from '../i18n';
import {
  homeKind,
  layerItems,
  type LayerModel,
  unitStack,
} from '../layers';
import { refsOf } from '../objConstraints';
import { type SheetClipItem } from '../sheetObjects';
import { titleEditor } from './titleEditor';
import {
  editedBg,
  LAYER_ENV,
  OBJECTS,
  pasteClipboard,
} from '../main';

/** This module's state that main.ts reads and writes too. */
export const clipboardUi = {
  // The schematic-object clipboard: an in-app copy of the selected sheet objects
  // (boxes, lines, connections, images), the paper-space twin of the title-block
  // clipboard. It is NOT the system clipboard — schematic ids are sheet-local, so a
  // cross-window paste has nothing to bind to (same reason title objects stay in-app).
  schObjClipboard: null as SheetClipItem[] | null,
  schObjPasteCount: 0,
  pasteCount: 0,
  clipboard: null as Clipboard | null,
};

// ---- Copy / paste of selected objects ----
export type Clipboard = ClipboardPayload;
const emptyClipboard = (): Clipboard => ({ ...emptyObjectArrays(), backgroundImages: [], groups: [], constraints: [] });
/** ONE clipboard: which space the last Copy / Cut came from. The three payload
 *  shapes stay (model objects, a sheet's objects, title-block objects — none can
 *  be pasted into another space), but only one of them is ever live: a copy in
 *  any space empties the other two, as the one system clipboard would. */
export type ClipSpace = 'model' | 'sheet' | 'title';
let clipSpace: ClipSpace | null = null;
export function takeClipboard(space: ClipSpace): void {
  clipSpace = space;
  if (space !== 'model') clipboardUi.clipboard = null;
  if (space !== 'sheet') clipboardUi.schObjClipboard = null;
  if (space !== 'title') titleEditor.titleObjClipboard = null;
}
/** Say where the clipboard CAN be pasted when this space can't take it. */
export function flashClipboardElsewhere(here: ClipSpace): void {
  if (!clipSpace || clipSpace === here) return;
  flashStatus(tr(clipSpace === 'model' ? 'The clipboard holds drawing objects — paste them in the Model tab'
    : clipSpace === 'sheet' ? 'The clipboard holds sheet objects — paste them on a sheet'
      : 'The clipboard holds title-block objects — paste them in the title-block editor'));
}
/** Duplicate borrows the clipboard; these put every slot back as it was. */
export function saveClipboards() {
  return { clipboard: clipboardUi.clipboard, pasteCount: clipboardUi.pasteCount, clipSpace, sch: clipboardUi.schObjClipboard, schN: clipboardUi.schObjPasteCount, title: titleEditor.titleObjClipboard, titleSrc: titleEditor.titleObjClipboardSourceCartId, titleN: titleEditor.titleObjPasteCount };
}
export function restoreClipboards(v: ReturnType<typeof saveClipboards>): void {
  ({ clipboard: clipboardUi.clipboard, pasteCount: clipboardUi.pasteCount, clipSpace } = v);
  clipboardUi.schObjClipboard = v.sch; clipboardUi.schObjPasteCount = v.schN;
  titleEditor.titleObjClipboard = v.title; titleEditor.titleObjClipboardSourceCartId = v.titleSrc; titleEditor.titleObjPasteCount = v.titleN;
}
export const PASTE_OFFSET = 300; // mm shift for each pasted copy
// The serial this window most recently put on, or took off, the system clipboard.
// Used so this window doesn't re-adopt its OWN copy (which would reset the growing
// paste offset); a serial from ANOTHER window differs and is adopted.
let lastClipSerial = '';

/** Record `cb` as the clipboard AND publish it to the system clipboard, so a copy
 *  here can be pasted in another StenalCAD window. Best-effort: a denied or
 *  unavailable clipboard just leaves the in-app copy working within this window
 *  (and the 'copy' event still places it synchronously for Ctrl+C). */
function commitCopiedClipboard(cb: Clipboard): void {
  takeClipboard('model');
  clipboardUi.clipboard = cb;
  clipboardUi.pasteCount = 0;
  const serial = serializeClipboard(cb);
  lastClipSerial = serial;
  try { void navigator.clipboard?.writeText(serial).catch(() => {}); } catch { /* ignore */ }
}

/** If `text` is a StenalCAD payload from a DIFFERENT copy (another window, or a
 *  newer copy in this one), adopt it as the clipboard and reset the paste offset.
 *  No-op for our own copy, empty text, or anything that isn't our payload. */
export function adoptSystemClipboard(text: string): void {
  if (!text || text === lastClipSerial) return;
  const cb = deserializeClipboard(text);
  if (!cb || !clipboardHasContent(cb)) return;
  takeClipboard('model');
  clipboardUi.clipboard = cb;
  clipboardUi.pasteCount = 0;
  lastClipSerial = text;
}

/** Paste objects, first reading a possibly-foreign payload off the system
 *  clipboard (for the menu / context-menu paste, which — unlike Ctrl+V — has no
 *  paste event to read synchronously). Falls back to the in-app clipboard if the
 *  read is denied or unavailable. */
export async function pasteObjectsFromSystem(at?: Vec2): Promise<void> {
  try {
    const text = await navigator.clipboard?.readText();
    if (text) adoptSystemClipboard(text);
  } catch { /* clipboard read denied/unavailable — use the in-app clipboard */ }
  pasteClipboard(at);
}

/** The current selection as a clipboard payload, or null when nothing copyable is
 *  selected: every selected object of every kind, as each kind's adapter says
 *  Copy takes it (drawingObjects.ts `copiesOfSelection`): a room with everything
 *  standing in it, the connections whose ends are all on copied objects, a
 *  schedule table never. A click selects a whole group, so a group copies whole;
 *  a member drilled into copies on its own. */
export function selectionClipboard(): Clipboard | null {
  const cb = emptyClipboard();

  // A selected imported underlay (PDF / image in transform mode) copies alone.
  if (state.editPdf) {
    const bg = editedBg();
    if (bg) { cb.backgroundImages = [structuredClone(bg)]; return cb; }
  }

  Object.assign(cb, copiesOfSelection(state.drawing, selectedObjects(state), OBJECTS));
  // The groups copied whole travel with the copy (nesting kept); a member copied
  // out of its group lands on its own.
  const copied = groupables(cb);
  const { groups, groupOf } = groupsForCopy(state.drawing, new Set(copied.map((o) => o.id)));
  cb.groups = groups;
  for (const o of copied) { const g = groupOf.get(o.id); if (g) o.groupId = g; else delete o.groupId; }
  // The constraints between copied objects travel with them (not Fix: a copy is
  // free to be put anywhere).
  const copiedIds = new Set(copied.map((o) => o.id));
  cb.constraints = objConstraints().filter((c) => c.type !== 'fix' && refsOf(c).every((r) => copiedIds.has(r.obj))).map((c) => structuredClone(c));
  stampCopiedLayers(STORED_KINDS.flatMap((k) => (objectsOf(cb, k) ?? []) as Array<{ id: string; layer?: string }>), cb.groups);
  // The stacking inside a copied group or room travels with it (`copyInnerOrder`).
  const copiedGroups = new Set(cb.groups.map((g) => g.id));
  cb.stack = unitStack(layers()).filter((id) => copiedIds.has(id) || copiedGroups.has(id));
  return clipboardHasContent(cb) ? cb : null;
}

/** The layer a copy of object `id` is told: its original's — or none when that
 *  is the kind's own home, or the original is nested in a room, so the copy
 *  goes home and nests in whatever room it lands in, like a new one. */
function copyLayerOf(m: LayerModel, id: string): string | undefined {
  const parent = m.parentOf.get(id);
  if (parent && m.unitOf.get(parent)?.kind === 'instance') return undefined; // nested in a room
  const lid = m.layerOf.get(id), u = m.unitOf.get(id);
  if (!lid || !u || u.kind === 'group') return lid;
  return lid === m.layers.find((l) => l.home === homeKind(u.item, LAYER_ENV))?.id ? undefined : lid;
}
function setCopyLayer(o: { layer?: string }, lid: string | undefined): void {
  if (lid) o.layer = lid; else delete o.layer;
}

/** A copy lands on its original's layer: each copied object (or group) is told
 *  the layer the original sat on (`copyLayerOf`). (A paste into a drawing
 *  without that layer falls back to the kind's own.) Takes the copies while
 *  they still carry the originals' ids. */
function stampCopiedLayers(copies: Array<{ id: string; layer?: string }>, groups: DrawingGroup[]): void {
  const m = layers();
  for (const o of copies) setCopyLayer(o, copyLayerOf(m, o.id));
  for (const g of groups) { const lid = m.layerOf.get(g.id); if (lid) g.layer = lid; }
}

/** Everything that sits on a layer now (objects and groups), by id. */
export function layerIdSet(): Set<string> {
  return new Set([...layerItems(state.drawing).map((i) => i.id), ...(state.drawing.groups || []).map((g) => g.id)]);
}

/** Copy the current selection to the clipboard (in-app and system). */
export function copySelection(): boolean {
  const cb = selectionClipboard();
  if (!cb) { flashNotCopied(); return false; }
  commitCopiedClipboard(cb);
  return true;
}

/** What is selected but copied with something else, or not at all, says so —
 *  Copy, Cut and Duplicate never do nothing without a word. */
export function flashNotCopied(): void {
  if (state.selectedConnId) flashStatus(tr('A connection is copied with the parts it joins — select them.'));
  else if (state.selectedScheduleTable) flashStatus(tr('A schedule table is not copied: the drawing has one of each.'));
}
